import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RoleBasedDashboard from "./pages/RoleBasedDashboard";
import Tasks from "./pages/Tasks";
import Team from "./pages/Team";
import DashboardLayout from "./components/DashboardLayout";
import NotificationOnboarding from "./components/NotificationOnboarding";
import ErrorBoundary from "./components/ErrorBoundary";
import SessionRecovery from "./components/SessionRecovery";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { Toaster } from "sonner";

// Lazy-loaded pages for better initial load performance
const Reports = React.lazy(() => import("./pages/Reports"));
const VAReports = React.lazy(() => import("./pages/VAReports"));
const Timesheets = React.lazy(() => import("./pages/Timesheets"));
const Performance = React.lazy(() => import("./pages/Performance"));
const Hire = React.lazy(() => import("./pages/Hire"));
const Leave = React.lazy(() => import("./pages/Leave"));
const Billing = React.lazy(() => import("./pages/Billing"));
const Settings = React.lazy(() => import("./pages/Settings"));
const VAProfile = React.lazy(() => import("./pages/VAProfile"));
const OAuthCallback = React.lazy(() => import("./pages/OAuthCallback"));
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail"));
const Chat = React.lazy(() => import("./pages/Chat"));
const SkillsAssessment = React.lazy(() => import("./pages/SkillsAssessment"));
const Portfolio = React.lazy(() => import("./pages/Portfolio"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const AdminCustomers = React.lazy(() => import("./pages/AdminCustomers"));
const SubscriptionAnalytics = React.lazy(() => import("./pages/SubscriptionAnalytics"));
const VAManagement = React.lazy(() => import("./pages/VAManagement"));
const Feedback = React.lazy(() => import("./pages/Feedback"));
const Attendance = React.lazy(() => import("./pages/Attendance"));
const Profile = React.lazy(() => import("./pages/Profile"));
const BulkAssign = React.lazy(() => import("./pages/BulkAssign"));
const VAComparison = React.lazy(() => import("./pages/VAComparison"));
const Analytics = React.lazy(() => import("./pages/admin/Analytics"));
const AdvancedReporting = React.lazy(() => import("./pages/admin/AdvancedReporting"));
const VAMatching = React.lazy(() => import("./pages/admin/VAMatching"));
const EmailTemplates = React.lazy(() => import("./pages/admin/EmailTemplates"));
const VAScheduling = React.lazy(() => import("./pages/admin/VAScheduling"));
const WeeklyDigest = React.lazy(() => import("./pages/admin/WeeklyDigest"));
const VAAssignments = React.lazy(() => import("./pages/VAAssignments"));
const VAAvailabilityCalendar = React.lazy(() => import("./pages/VAAvailabilityCalendar"));
const NotificationPreferences = React.lazy(() => import("./pages/NotificationPreferences"));
const VAPerformanceHeatmap = React.lazy(() => import("./pages/VAPerformanceHeatmap"));
const VAUtilizationReport = React.lazy(() => import("./pages/VAUtilizationReport"));
const ClientOnboarding = React.lazy(() => import("./pages/ClientOnboarding"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="w-8 h-8 text-[#5FD068] animate-spin" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  // Force light mode — dark mode removed
  React.useEffect(() => {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  }, []);

  return (
    <ErrorBoundary>
      <WebSocketProvider>
      <div className="App">
        <BrowserRouter>
          <SessionRecovery />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/oauth-callback" element={<OAuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* All protected routes share a single DashboardLayout instance */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<RoleBasedDashboard />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/team" element={<Team />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/timesheets" element={<Timesheets />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/hire" element={<Hire />} />
              <Route path="/leave" element={<Leave />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/va/:vaId" element={<VAProfile />} />
              <Route path="/team/:vaId" element={<VAProfile />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/assessments" element={<SkillsAssessment />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/va-reports" element={<VAReports />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/analytics" element={<SubscriptionAnalytics />} />
              <Route path="/admin/va-management" element={<VAManagement />} />
              <Route path="/admin/bulk-assign" element={<BulkAssign />} />
              <Route path="/admin/va-comparison" element={<VAComparison />} />
              <Route path="/admin/advanced-analytics" element={<Analytics />} />
              <Route path="/admin/va-matching" element={<VAMatching />} />
              <Route path="/admin/email-templates" element={<EmailTemplates />} />
              <Route path="/admin/va-scheduling" element={<VAScheduling />} />
              <Route path="/admin/weekly-digest" element={<WeeklyDigest />} />
              <Route path="/admin/advanced-reporting" element={<AdvancedReporting />} />
              <Route path="/admin/va-assignments" element={<VAAssignments />} />
              <Route path="/admin/va-availability" element={<VAAvailabilityCalendar />} />
              <Route path="/admin/va-heatmap" element={<VAPerformanceHeatmap />} />
              <Route path="/admin/va-utilization" element={<VAUtilizationReport />} />
              <Route path="/notification-preferences" element={<NotificationPreferences />} />
              <Route path="/onboarding" element={<ClientOnboarding />} />
            </Route>
            
            {/* Catch-all: redirect unknown routes to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <NotificationOnboarding />
        <Toaster position="top-right" richColors />
      </div>
      </WebSocketProvider>
    </ErrorBoundary>
  );
}

export default App;
